import { Dialog, DialogProps, Box } from '@mui/material';
import useId from '@mui/material/utils/useId';

// 💡 Use Omit to prevent the TypeScript 'role' conflict
type AccessibleDialogProps = Omit<DialogProps, 'role'> & {
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  role?: string;
};

const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  width: '1px',
} as const;

export const AccessibleDialog = ({
  open,
  onClose,
  title,
  description,
  children,
  role = 'dialog',
  ...rest
}: AccessibleDialogProps) => {
  const id = useId();
  const titleId = `${id}-title`;
  const descId = `${id}-description`;

  // 💡 Fallback title for screen readers
  const accessibleTitle = title || 'Information';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Always link the titleId since we now have a fallback
      aria-labelledby={titleId}
      PaperProps={{
        role,
        ...rest.PaperProps,
      }}
      {...rest}>
      {/* 
         Visually hidden but accessible. 
         We always render this now to ensure the dialog has a name. 
      */}
      <Box component="span" id={titleId} sx={visuallyHidden}>
        {accessibleTitle}
      </Box>

      {description && (
        <Box component="span" id={descId} sx={visuallyHidden}>
          {description}
        </Box>
      )}

      {children}
    </Dialog>
  );
};
